import { create } from 'zustand';
import apiClient from '../lib/api';

export interface OffboardingRecord {
    id: string;
    tenantId: string;
    employeeId: string;
    employeeFirstName?: string;
    employeeLastName?: string;
    employeeEmail?: string;
    resignationDate?: string;
    lastWorkingDay?: string;
    reason?: string;
    status: string;
    exitInterviewCompleted: boolean;
    assetsRecovered: boolean;
    /** Live from the Asset Hub: assets still assigned / licence seats still held. */
    assetsHeld?: number | null;
    licensesHeld?: number | null;
    createdAt: string;
    updatedAt: string;
}

interface OffboardingState {
    records: OffboardingRecord[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
    };
    fetchRecords: (filters?: any) => Promise<void>;
    createRecord: (data: Partial<OffboardingRecord>) => Promise<OffboardingRecord>;
    updateRecord: (id: string, data: Partial<OffboardingRecord>) => Promise<OffboardingRecord>;
    deleteRecord: (id: string) => Promise<void>;
}

export const useOffboardingStore = create<OffboardingState>((set, get) => ({
    records: [],
    isLoading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
    },
    fetchRecords: async (filters?: any) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/offboarding', { params: filters });
            const data = response.data?.data ?? response.data;
            const pagination = response.data?.pagination ?? { page: 1, limit: 20, total: Array.isArray(data) ? data.length : 0, totalPages: 1 };
            set({ records: Array.isArray(data) ? data : [], pagination, isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch offboarding records', isLoading: false });
        }
    },
    createRecord: async (data: Partial<OffboardingRecord>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/people/offboarding', data);
            const newRecord = response.data?.data ?? response.data;
            set((state) => ({
                records: [newRecord, ...state.records],
                isLoading: false,
            }));
            return newRecord;
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create offboarding record', isLoading: false });
            throw error;
        }
    },
    updateRecord: async (id: string, data: Partial<OffboardingRecord>) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.patch(`/people/offboarding/${id}`, data);
            const updated = response.data?.data ?? response.data;
            // The PATCH response carries no employee join, so merge instead of
            // replacing — otherwise the row loses its name and holdings.
            const defined = Object.fromEntries(Object.entries(updated || {}).filter(([, v]) => v !== undefined && v !== null));
            let merged = updated;
            set((state) => ({
                records: state.records.map((o) => (o.id === id ? (merged = { ...o, ...defined }) : o)),
                isLoading: false,
            }));
            return merged;
        } catch (error: any) {
            const message = error.response?.data?.message || 'Failed to update offboarding record';
            set({ error: message, isLoading: false });
            throw new Error(message);
        }
    },
    deleteRecord: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
            await apiClient.delete(`/people/offboarding/${id}`);
            set((state) => ({
                records: state.records.filter((o) => o.id !== id),
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete offboarding record', isLoading: false });
            throw error;
        }
    },
}));
